import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, ShieldCheck, X } from 'lucide-react';
import type { AuditLogRowDto } from '@gc/shared';

import { FilterDisclosure } from '@/components/layout/FilterDisclosure';
import { useAuth } from '@/features/auth/useAuth';
import { ApiError } from '@/lib/api';
import {
  AUDIT_AREAS,
  AUDIT_ENTITY_TYPES,
  EMPTY_AUDIT_FILTERS,
  auditActionKey,
  auditAreaKey,
  auditChangeRows,
  formatAuditValue,
  isAuditAreaPrefix,
  unchangedCount,
  type AuditFilters,
} from './auditLabels';
import { useAuditActors, useAuditLog, type AuditActor } from './useAuditLog';

/**
 * The audit log — SPEC §3, `audit.view` (owner, 2026-09-23: "implement the
 * five privileges so it all works correctly"). Every sensitive mutation has
 * written a row since the first wave; this is the first screen that reads
 * them: newest first, filtered, a page at a time, each row opening onto what
 * it changed.
 *
 * READ-ONLY, for everyone. There is no edit or delete here and none in the
 * API — a log its subject could prune would not be a control.
 *
 * The automatic PC screen pushes (desktop hand-over, relock, seal) are hidden
 * by default: they are two thirds of production's rows, and on a first page
 * of nothing else the bill edits and waived charges an owner opens this for
 * would be pages down. One tick brings them back.
 */
export function AuditLogPage() {
  const { t } = useTranslation();
  const { can, venue } = useAuth();
  const allowed = can('audit.view');
  const currencySymbol = venue?.currencySymbol ?? 'IQD';

  const [filters, setFilters] = useState<AuditFilters>(EMPTY_AUDIT_FILTERS);
  const [searchDraft, setSearchDraft] = useState('');
  const actors = useAuditActors(allowed);
  const { rows, nextCursor, loading, loadingMore, error, loadMore } = useAuditLog(filters, allowed);

  // Typing is not a query per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((current) =>
        current.q === searchDraft ? current : { ...current, q: searchDraft },
      );
    }, 400);
    return () => clearTimeout(timer);
  }, [searchDraft]);

  const set = (patch: Partial<AuditFilters>) => setFilters((current) => ({ ...current, ...patch }));

  if (!allowed) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-content">{t('auditLog.title')}</h1>
        <p className="mt-2 text-sm text-content-muted">{t('auditLog.noPermission')}</p>
      </div>
    );
  }

  const controlClass =
    'rounded-control border border-subtle bg-input px-2.5 py-1.5 text-sm text-content outline-none focus:border-primary';
  const hiddenFilterCount =
    (filters.from ? 1 : 0) +
    (filters.to ? 1 : 0) +
    (filters.employeeId ? 1 : 0) +
    (filters.action ? 1 : 0) +
    (filters.entityType ? 1 : 0) +
    (filters.showRoutine ? 1 : 0);
  const anyFilter =
    hiddenFilterCount > 0 || filters.entityId !== '' || filters.q !== '' || searchDraft !== '';
  const exactAction = filters.action !== '' && !isAuditAreaPrefix(filters.action);

  return (
    <div className="flex h-full flex-col overflow-auto">
      <header className="px-4 pt-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-content">
          <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
          {t('auditLog.title')}
        </h1>
        <p className="mt-1 text-xs text-content-muted">{t('auditLog.subtitle')}</p>
      </header>

      <div className="flex flex-wrap items-end gap-3 p-4">
        <label className="relative block">
          <span className="sr-only">{t('auditLog.filters.search')}</span>
          <Search
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted"
            style={{ insetInlineStart: '0.6rem' }}
            aria-hidden
          />
          <input
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder={t('auditLog.filters.searchPlaceholder')}
            maxLength={120}
            className={`${controlClass} w-56 ps-8 sm:w-72`}
          />
        </label>

        <FilterDisclosure badge={hiddenFilterCount}>
          <label className="block">
            <span className="mb-1 block text-xs text-content-muted">
              {t('auditLog.filters.from')}
            </span>
            <input
              type="datetime-local"
              value={filters.from}
              onChange={(event) => set({ from: event.target.value })}
              className={`${controlClass} tabular`}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs text-content-muted">
              {t('auditLog.filters.to')}
            </span>
            <input
              type="datetime-local"
              value={filters.to}
              onChange={(event) => set({ to: event.target.value })}
              className={`${controlClass} tabular`}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs text-content-muted">
              {t('auditLog.filters.person')}
            </span>
            <select
              value={filters.employeeId}
              onChange={(event) => set({ employeeId: event.target.value })}
              className={controlClass}
            >
              <option value="">{t('auditLog.filters.anyone')}</option>
              {actors.map((actor) => (
                <option key={actor.id} value={actor.id}>
                  {actorLabel(actor, t)}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs text-content-muted">
              {t('auditLog.filters.area')}
            </span>
            <select
              value={filters.action}
              onChange={(event) => set({ action: event.target.value })}
              className={controlClass}
            >
              <option value="">{t('auditLog.filters.anyArea')}</option>
              {AUDIT_AREAS.map((prefix) => (
                <option key={prefix} value={prefix}>
                  {t(auditAreaKey(prefix))}
                </option>
              ))}
              {exactAction ? (
                <option value={filters.action}>
                  {t(auditActionKey(filters.action), { defaultValue: filters.action })}
                </option>
              ) : null}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs text-content-muted">
              {t('auditLog.filters.record')}
            </span>
            <select
              value={filters.entityType}
              onChange={(event) => set({ entityType: event.target.value, entityId: '' })}
              className={controlClass}
            >
              <option value="">{t('auditLog.filters.anyRecord')}</option>
              {AUDIT_ENTITY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`auditLog.entities.${type}`, { defaultValue: type })}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 self-center text-sm text-content-secondary">
            <input
              type="checkbox"
              checked={filters.showRoutine}
              onChange={(event) => set({ showRoutine: event.target.checked })}
            />
            {t('auditLog.filters.showRoutine')}
          </label>
        </FilterDisclosure>

        {anyFilter ? (
          <button
            type="button"
            onClick={() => {
              setFilters(EMPTY_AUDIT_FILTERS);
              setSearchDraft('');
            }}
            className="rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover"
          >
            {t('auditLog.filters.clear')}
          </button>
        ) : null}
      </div>

      {filters.entityId ? (
        <div className="flex flex-wrap gap-2 px-4 pb-2">
          <FilterChip
            label={t('auditLog.filters.onlyRecord', {
              type: t(`auditLog.entities.${filters.entityType}`, {
                defaultValue: filters.entityType,
              }),
              id: shortId(filters.entityId),
            })}
            onRemove={() => set({ entityType: '', entityId: '' })}
          />
        </div>
      ) : null}

      <div className="min-h-0 flex-1 px-4 pb-6">
        {error && rows.length === 0 ? (
          <p role="alert" className="text-sm text-danger">
            {error instanceof ApiError && error.status !== 500
              ? error.message
              : t('auditLog.loadFailed')}
          </p>
        ) : loading && rows.length === 0 ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-content-muted">{t('auditLog.empty')}</p>
        ) : (
          <>
            <AuditLogList
              rows={rows}
              currencySymbol={currencySymbol}
              onFilterEmployee={(employeeId) => set({ employeeId })}
              onFilterAction={(action) => set({ action })}
              onFilterRecord={(entityType, entityId) => set({ entityType, entityId })}
            />
            <div className="mt-4 flex justify-center">
              {nextCursor ? (
                <button
                  type="button"
                  onClick={() => void loadMore()}
                  disabled={loadingMore}
                  className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover disabled:opacity-60"
                >
                  {loadingMore ? t('common.loading') : t('auditLog.loadMore')}
                </button>
              ) : (
                <p className="text-xs text-content-muted">{t('auditLog.end')}</p>
              )}
            </div>
            {error && rows.length > 0 ? (
              <p role="alert" className="mt-2 text-center text-sm text-danger">
                {t('auditLog.loadFailed')}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function actorLabel(
  actor: AuditActor,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  return actor.isActive ? actor.name : t('auditLog.filters.inactive', { name: actor.name });
}

/** The tail of an id — enough to tell two apart on screen; the title has it whole. */
function shortId(id: string): string {
  return id.length > 8 ? `…${id.slice(-6)}` : id;
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs text-content">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={t('auditLog.filters.remove')}
        title={t('auditLog.filters.remove')}
        className="rounded-full p-0.5 text-content-secondary hover:text-content"
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </span>
  );
}

/**
 * The rows, presentational — exported so the list can be pinned by static
 * markup without an API behind it. Each person, action and record is a
 * button that narrows the list to it: "what else did she do tonight" is one
 * press, not a trip back to the filters.
 */
export function AuditLogList({
  rows,
  currencySymbol,
  expandedIds = [],
  onFilterEmployee,
  onFilterAction,
  onFilterRecord,
}: {
  rows: readonly AuditLogRowDto[];
  currencySymbol: string;
  /** Rows open on first render — for tests and deep links. */
  expandedIds?: readonly string[];
  onFilterEmployee: (employeeId: string) => void;
  onFilterAction: (action: string) => void;
  onFilterRecord: (entityType: string, entityId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-card border border-subtle">
      <div className="hidden grid-cols-[11rem_9rem_minmax(0,1fr)_10rem_2.5rem] gap-3 bg-overlay px-3 py-2 text-[10px] uppercase tracking-[0.14em] text-content-muted md:grid">
        <span>{t('auditLog.columns.when')}</span>
        <span>{t('auditLog.columns.who')}</span>
        <span>{t('auditLog.columns.what')}</span>
        <span>{t('auditLog.columns.record')}</span>
        <span />
      </div>
      <ul>
        {rows.map((row) => (
          <AuditRowView
            key={row.id}
            row={row}
            currencySymbol={currencySymbol}
            defaultExpanded={expandedIds.includes(row.id)}
            onFilterEmployee={onFilterEmployee}
            onFilterAction={onFilterAction}
            onFilterRecord={onFilterRecord}
          />
        ))}
      </ul>
    </div>
  );
}

function AuditRowView({
  row,
  currencySymbol,
  defaultExpanded,
  onFilterEmployee,
  onFilterAction,
  onFilterRecord,
}: {
  row: AuditLogRowDto;
  currencySymbol: string;
  defaultExpanded: boolean;
  onFilterEmployee: (employeeId: string) => void;
  onFilterAction: (action: string) => void;
  onFilterRecord: (entityType: string, entityId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const when = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(new Date(row.createdAt));
  const who = row.employeeName ?? row.actorLabel ?? t('auditLog.system');
  const label = t(auditActionKey(row.action), { defaultValue: row.action });
  const reason = reasonOf(row.after);
  const entityLabel = t(`auditLog.entities.${row.entityType}`, { defaultValue: row.entityType });
  const linkClass = 'truncate text-start hover:text-primary hover:underline';

  return (
    <li
      className="border-t border-subtle first:border-t-0"
      data-audit-row={row.id}
      data-action={row.action}
    >
      <div className="grid grid-cols-[minmax(0,1fr)_2.5rem] gap-x-3 gap-y-0.5 px-3 py-2 text-sm md:grid-cols-[11rem_9rem_minmax(0,1fr)_10rem_2.5rem] md:items-center">
        <span className="tabular text-xs text-content-muted md:text-sm md:text-content-secondary">
          {when}
        </span>
        <span className="col-start-1 md:col-start-auto">
          {row.employeeId ? (
            <button
              type="button"
              onClick={() => onFilterEmployee(row.employeeId!)}
              title={t('auditLog.filterByPerson', { name: who })}
              className={`${linkClass} font-medium text-content`}
            >
              {who}
            </button>
          ) : (
            <span className="text-content-secondary">{who}</span>
          )}
        </span>
        <span className="col-start-1 min-w-0 md:col-start-auto">
          <button
            type="button"
            onClick={() => onFilterAction(row.action)}
            title={t('auditLog.filterByAction')}
            className={`${linkClass} block text-content`}
          >
            {label}
          </button>
          <span className="block truncate font-mono text-[11px] text-content-muted">
            {row.action}
          </span>
          {reason ? (
            <span className="block truncate text-xs text-content-secondary">
              {t('auditLog.reason', { reason })}
            </span>
          ) : null}
        </span>
        <span className="col-start-1 min-w-0 text-xs text-content-secondary md:col-start-auto">
          {row.entityId ? (
            <button
              type="button"
              onClick={() => onFilterRecord(row.entityType, row.entityId!)}
              title={`${t('auditLog.filterByRecord')} — ${row.entityId}`}
              className={linkClass}
            >
              {entityLabel} {shortId(row.entityId)}
            </button>
          ) : (
            entityLabel
          )}
        </span>
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-label={expanded ? t('auditLog.hideDetails') : t('auditLog.details')}
          title={expanded ? t('auditLog.hideDetails') : t('auditLog.details')}
          className="col-start-2 row-span-4 row-start-1 grid h-8 w-8 place-items-center self-start rounded-full text-content-secondary hover:bg-hover md:col-start-auto md:row-span-1 md:row-start-auto md:self-center"
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
      </div>
      {expanded ? (
        <div className="border-t border-subtle bg-overlay/40 px-3 py-3">
          <AuditChanges before={row.before} after={row.after} currencySymbol={currencySymbol} />
        </div>
      ) : null}
    </li>
  );
}

/** A `reason` the writer stored on the after-half — bill edits, voids, moves, unlocks. */
function reasonOf(after: unknown): string | null {
  if (typeof after !== 'object' || after === null || Array.isArray(after)) return null;
  const reason = (after as Record<string, unknown>).reason;
  return typeof reason === 'string' && reason.trim() ? reason : null;
}

/**
 * Before and after, field by field — changed fields only until asked. A pair
 * that is not two plain objects is shown as the JSON it is.
 */
export function AuditChanges({
  before,
  after,
  currencySymbol,
  initiallyShowUnchanged = false,
}: {
  before: unknown;
  after: unknown;
  currencySymbol: string;
  initiallyShowUnchanged?: boolean;
}) {
  const { t } = useTranslation();
  const [showUnchanged, setShowUnchanged] = useState(initiallyShowUnchanged);
  const rows = auditChangeRows(before, after, showUnchanged);
  const hidden = unchangedCount(before, after);

  const show = (key: string, value: unknown) =>
    value === '[redacted]' ? (
      <span className="italic text-content-muted">{t('auditLog.redacted')}</span>
    ) : (
      formatAuditValue(key, value, {
        currencySymbol,
        yes: t('auditLog.yes'),
        no: t('auditLog.no'),
        empty: '—',
      })
    );

  if (rows === null) {
    return (
      <div className="grid gap-2 md:grid-cols-2">
        {[
          ['before', before],
          ['after', after],
        ].map(([side, value]) => (
          <div key={side as string}>
            <p className="mb-1 text-[10px] uppercase tracking-[0.14em] text-content-muted">
              {t(`auditLog.${side as string}`)}
            </p>
            <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-control bg-input p-2 font-mono text-xs text-content">
              {value == null ? '—' : JSON.stringify(value, null, 2)}
            </pre>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {rows.length === 0 ? (
        <p className="text-xs text-content-muted">{t('auditLog.noFields')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-max text-xs">
            <thead className="text-[10px] uppercase tracking-[0.14em] text-content-muted">
              <tr>
                <th className="py-1 pe-3 text-start font-normal">{t('auditLog.field')}</th>
                <th className="py-1 pe-3 text-start font-normal">{t('auditLog.before')}</th>
                <th className="py-1 text-start font-normal">{t('auditLog.after')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((change) => (
                <tr
                  key={change.key}
                  data-field={change.key}
                  data-changed={change.changed || undefined}
                  className="border-t border-subtle align-top"
                >
                  <td className="py-1 pe-3 font-mono text-content-secondary">{change.key}</td>
                  <td
                    className={`py-1 pe-3 ${change.changed ? 'text-danger line-through decoration-danger/50' : 'text-content-secondary'}`}
                  >
                    {change.onlyOn === 'after' ? '' : show(change.key, change.before)}
                  </td>
                  <td
                    className={`py-1 ${change.changed ? 'font-medium text-content' : 'text-content-secondary'}`}
                  >
                    {change.onlyOn === 'before' ? '' : show(change.key, change.after)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hidden > 0 ? (
        <button
          type="button"
          onClick={() => setShowUnchanged((open) => !open)}
          className="mt-2 text-xs text-primary hover:underline"
        >
          {showUnchanged
            ? t('auditLog.hideUnchanged')
            : t('auditLog.showUnchanged', { count: hidden })}
        </button>
      ) : null}
    </div>
  );
}
